/**
 * moneyCommit.ts — the order a money correction happens in once a signed instrument quotes it.
 *
 * VALIDATE → VOID → WRITE MONEY → REISSUE → SWEEP, and every arrow is load-bearing.
 *
 * VALIDATE FIRST BECAUSE THE VOID IS NO LONGER LAST. While the writes ran first, a typo cost
 * nothing: `saveSalePrice` refused it before a document could be withdrawn. Void-first gives that
 * away, so the three refusals are read here — from the same predicates the writes raise, so the
 * sentence an admin gets is the sentence `CLAUSE_REFUSALS` files under the field.
 *
 * VOID BEFORE THE WRITE, WHICH IS THE CORRECTION D-B42 CARRIES. Contract #319 holds `sold_at`
 * 211,285.717 while its signed hire purchase document goes on quoting 266,185.72: the money was
 * written, the void was then refused, and nothing put the two back together. A void that is going
 * to be refused has to be refused before the columns move.
 *
 * AND A WRITE THAT IS GOING TO BE REFUSED HAS TO BE REFUSED BEFORE THE VOID [D-B59]. On a DRAFT
 * contract an executed Vehicle Schedule makes the database refuse a cash-price or selling-price
 * write (`PARTNER_SCHEDULE_RECORD_IMMUTABLE`). Left to the server, that refusal lands after the void
 * has already spent the driver's signature on the hire purchase agreement: #319's shape, arriving
 * from the other side. So VALIDATE reads the canvas's own `partnerScheduleMoneyNote` and refuses
 * first.
 *
 * THE WRITE BEFORE THE REISSUE, because a fresh document renders from the columns — reissuing
 * first would freeze the previous figures into a brand new document. And the void before the
 * create at all, because the partial unique index behind an arc admits one non-VOIDED document per
 * subject: a create ahead of the void hands back the document that quotes the old money.
 *
 * THE SWEEP IS LAST, AND IT IS THE ONE STEP NO FAILURE OF IT IS WORTH REPORTING [D-B44]. A
 * withdrawal strands the storage objects the deleted document addressed; the sweep is the client's
 * half of clearing them and it is fired once per gesture, after every create, on both paths. It
 * cannot fail the commit: the ledger it drains is read whole on every call, so a sweep that never
 * ran leaves exactly what a sweep that failed leaves, and the next one takes both.
 *
 * THE CREATE IS GUARANTEED, NOT CONDITIONAL. Whatever this call actually withdrew is reissued, on
 * the failure path as much as the happy one — a void that succeeded and a write that then failed
 * would otherwise leave the arc with no document at all, and an admin looking at an empty slot
 * cannot tell a correction in progress from a document somebody deleted. The reissue renders LIVE
 * until it is frozen, so a retried save writes the money into a document that follows it and needs
 * no second void.
 *
 * PURE, AND REACT-FREE. Every side effect arrives in `effects`, which is what lets the order above
 * be pinned by a call log rather than by a screenshot.
 */

import { cashPriceRefusal, instalmentRefusal, salePriceRefusal } from './data/financials';
import type { HazardDocType, PartnerScheduleMoneyNote } from './hazards';

/** The Money particulars as the block holds them — typed strings, the blank included. */
export interface MoneyDraft {
    cashPrice: string;
    price: string;
    instalmentAmount: string;
    instalmentStart: string;
}

/** A live document this commit withdraws and reissues. */
export interface MoneyInstrument {
    docType: HazardDocType;
    /** What `agreement_create` is called with for the replacement. */
    subjectId: number;
    documentId: number | null;
}

export interface MoneyCommitPlan {
    contractId: number;
    /** The row `saveContractInstalment` amends; null creates one. */
    existingInstalmentId: number | null;
    draft: MoneyDraft;
    /** Which fields differ from the server read. Nothing else is written. */
    changed: readonly string[];
    /** Which fields the admin has actually typed into — the cash price's blank turns on this. */
    touched: readonly string[];
    /** The documents to withdraw and reissue, in board order. Empty is the ordinary save. */
    voidable: readonly MoneyInstrument[];
    /** What the records list shows in a withdrawn document's place. */
    reason: string | null;
    /**
     * What the server does to the Vehicle Schedule when this save moves the money:
     * `partnerScheduleMoneyNote` over the same board [D-B59]. A `refused` note stops a cash-price
     * or selling-price write before anything is voided; see VALIDATE. Null where the contract has
     * no live schedule or is past DRAFT.
     */
    scheduleNote: PartnerScheduleMoneyNote | null;
}

export interface MoneyCommitEffects {
    saveCashPrice: (contractId: number, cashPrice: string) => Promise<unknown>;
    saveSalePrice: (contractId: number, soldAt: string) => Promise<unknown>;
    saveContractInstalment: (input: {
        contractId: number;
        existingInstalmentId: number | null;
        amount: string;
        startsAt: string;
    }) => Promise<unknown>;
    voidAgreement: (documentId: number, reason: string | null) => Promise<unknown>;
    createAgreement: (docType: HazardDocType, subjectId: number) => Promise<unknown>;
    /**
     * The bytes the withdrawals stranded [D-B44]. ONE CALL PER GESTURE, NOT PER VOID — the sweep
     * reads the whole ledger every time, so three withdrawals are drained by one pass and a second
     * pass would only re-read an empty list.
     */
    sweepOrphans: () => Promise<unknown>;
}

export async function commitMoneyChange(
    plan: MoneyCommitPlan,
    effects: MoneyCommitEffects,
): Promise<void> {
    /**
     * RULE 6, AND THE CASH PRICE IS THE ONE FIELD ON THE BLOCK IT BITES ON. The other two writes
     * refuse an empty figure, so a draft seeded `''` while the contract read was still in flight
     * can only ever cost them a refusal. `saveCashPrice` CLEARS on empty — that is how a price
     * typed against the wrong contract is taken back — so the same stale blank would silently wipe
     * a recorded cash price instead. `touched` is the only evidence the blank is somebody's
     * statement rather than a value that had not arrived yet.
     */
    const writesCash = plan.changed.includes('cashPrice') && plan.touched.includes('cashPrice');
    const writesPrice = plan.changed.includes('price');
    const writesInstalment =
        plan.changed.includes('instalmentAmount') || plan.changed.includes('instalmentStart');

    // ── VALIDATE ─────────────────────────────────────────────────────────────
    /**
     * THE EXECUTED VEHICLE SCHEDULE FIRST [D-B59], because no figure typed into either field can
     * pass it. `trg_contracts_void_partner_schedule` watches `cash_price` and `sold_at` (and the fee
     * and the vehicle, which this block does not write), so only those two writes meet it; the
     * instalment is not watched and saves past it. Refused here, the hire purchase agreement the
     * confirm offered to void is still standing when the admin reads the sentence.
     */
    const scheduleRefusal =
        (writesCash || writesPrice) && plan.scheduleNote?.kind === 'refused'
            ? plan.scheduleNote.sentence
            : null;
    const refusal =
        scheduleRefusal ??
        (writesCash ? cashPriceRefusal(plan.draft.cashPrice) : null) ??
        (writesPrice ? salePriceRefusal(plan.draft.price) : null) ??
        (writesInstalment
            ? instalmentRefusal({
                  amount: plan.draft.instalmentAmount,
                  startsAt: plan.draft.instalmentStart,
              })
            : null);
    if (refusal) throw new Error(refusal);

    /**
     * WHAT WAS WITHDRAWN, WHICH IS WHAT GETS REISSUED. An instrument carrying no document id is
     * skipped by both loops rather than by one: `isVoidable` already excludes it, and creating
     * against an arc this call did not clear is the case the unique index refuses.
     */
    const withdrawn: MoneyInstrument[] = [];

    try {
        // ── VOID ─────────────────────────────────────────────────────────────
        for (const instrument of plan.voidable) {
            if (instrument.documentId === null) continue;
            await effects.voidAgreement(instrument.documentId, plan.reason);
            withdrawn.push(instrument);
        }

        // ── WRITE MONEY ──────────────────────────────────────────────────────
        if (writesCash) await effects.saveCashPrice(plan.contractId, plan.draft.cashPrice);
        if (writesPrice) await effects.saveSalePrice(plan.contractId, plan.draft.price);
        if (writesInstalment) {
            await effects.saveContractInstalment({
                contractId: plan.contractId,
                existingInstalmentId: plan.existingInstalmentId,
                amount: plan.draft.instalmentAmount,
                startsAt: plan.draft.instalmentStart,
            });
        }
    } catch (failure) {
        // BEST-EFFORT, AND SILENT BY DESIGN. The refusal the admin has to act on is the one raised
        // above; a reissue that fails on top of it must not take its place.
        for (const instrument of withdrawn) {
            await effects
                .createAgreement(instrument.docType, instrument.subjectId)
                .catch(() => undefined);
        }
        // AND THE SWEEP FOR THE SAME REASON, AFTER THE GUARANTEED CREATES AND BEFORE THE RETHROW:
        // the withdrawals happened, so their bytes are stranded whether or not the write that
        // followed them did. The catch is this orchestrator's job rather than the caller's —
        // `failure` is the sentence the admin has to act on, and a sweep that threw over it would
        // displace it with one about storage.
        if (withdrawn.length > 0) await effects.sweepOrphans().catch(() => undefined);
        throw failure;
    }

    // ── REISSUE ──────────────────────────────────────────────────────────────
    for (const instrument of withdrawn) {
        await effects.createAgreement(instrument.docType, instrument.subjectId);
    }

    /**
     * ── SWEEP, LAST AND ONLY WHERE SOMETHING WAS WITHDRAWN [D-B44] ───────────
     *
     * `agreement_void` records the addresses it can no longer compose and cannot delete the
     * objects; this is the other half. It goes last because a create can strand nothing, and it is
     * skipped entirely on the ordinary save because a save that withdrew nothing left no bytes
     * behind — an unconditional call would fire the edge function on every price correction on
     * every contract to be told there is nothing to do.
     */
    if (withdrawn.length > 0) await effects.sweepOrphans().catch(() => undefined);
}
